import type { CtaSection, FaqSection, FeaturesSection, LogosSection, StatsSection, TimelineSection } from "../../content/industry";
import { Icon } from "./Icon";
import { Photo } from "./Photo";
import { SectionHeading } from "./SectionHeading";

/** Características con icono, en lista o en tarjetas. */
export function Features({ section }: { section: FeaturesSection }) {
  return (
    <section id={section.id} className={`ix-section ix-features ix-features--${section.variant}`} aria-labelledby={`${section.id}-title`}>
      <div className="ix-container">
        <SectionHeading id={section.id} eyebrow={section.eyebrow} title={section.title} text={section.text} />
        <ul className="ix-grid ix-grid--3">{section.items.map(item => <li key={item.title} className="ix-feature">
          {item.icon ? <span className="ix-feature-icon"><Icon name={item.icon} /></span> : null}
          <h3>{item.title}</h3><p>{item.text}</p>
        </li>)}</ul>
      </div>
    </section>
  );
}

/** Cifras destacadas (usa solo datos verificables). */
export function Stats({ section }: { section: StatsSection }) {
  return (
    <section id={section.id} className="ix-section ix-stats-band" aria-label={section.title ?? "Cifras"}>
      <div className="ix-container">
        {section.title ? <div className="ix-heading">{section.eyebrow ? <p className="ix-eyebrow">{section.eyebrow}</p> : null}<h2>{section.title}</h2></div> : null}
        <dl className="ix-stats-list">{section.items.map(item => <div key={item.label}><dd>{item.value}</dd><dt>{item.label}</dt></div>)}</dl>
        {section.note ? <p className="ix-note">{section.note}</p> : null}
      </div>
    </section>
  );
}

/** Preguntas frecuentes con details/summary: accesibles con teclado y sin JavaScript. */
export function Faq({ section }: { section: FaqSection }) {
  return (
    <section id={section.id} className="ix-section ix-faq" aria-labelledby={`${section.id}-title`}>
      <div className="ix-container ix-faq-grid">
        <SectionHeading id={section.id} eyebrow={section.eyebrow} title={section.title} />
        <div className="ix-faq-list">{section.items.map(item => <details key={item.question}><summary>{item.question}</summary><p>{item.answer}</p></details>)}</div>
      </div>
    </section>
  );
}

export function Timeline({ section }: { section: TimelineSection }) {
  return (
    <section id={section.id} className="ix-section ix-timeline" aria-labelledby={`${section.id}-title`}>
      <div className="ix-container">
        <SectionHeading id={section.id} eyebrow={section.eyebrow} title={section.title} />
        <ol className="ix-timeline-list">{section.items.map(item => <li key={item.year + item.title}><span className="ix-year">{item.year}</span><div><h3>{item.title}</h3><p>{item.text}</p></div></li>)}</ol>
      </div>
    </section>
  );
}

/** Logotipos de clientes como marcas de texto (reemplázalos solo con clientes que autoricen aparecer). */
export function Logos({ section }: { section: LogosSection }) {
  return (
    <section id={section.id} className="ix-section ix-logos" aria-labelledby={`${section.id}-title`}>
      <div className="ix-container">
        <h2 id={`${section.id}-title`} className="ix-logos-title">{section.title}</h2>
        <ul className="ix-logos-list">{section.items.map(item => <li key={item}>{item}</li>)}</ul>
        {section.note ? <p className="ix-note">{section.note}</p> : null}
      </div>
    </section>
  );
}

export function Cta({ section }: { section: CtaSection }) {
  return (
    <section id={section.id} className={section.image !== undefined ? "ix-section ix-cta has-image" : "ix-section ix-cta"} aria-labelledby={`${section.id}-title`}>
      {section.image !== undefined ? <div className="ix-cta-media"><Photo index={section.image} /></div> : null}
      <div className="ix-container ix-cta-inner">
        {section.eyebrow ? <p className="ix-eyebrow">{section.eyebrow}</p> : null}
        <h2 id={`${section.id}-title`}>{section.title}</h2>
        <p className="ix-lead">{section.text}</p>
        <div className="ix-actions">
          <a className="ix-button ix-button--primary" href={section.primary.href}>{section.primary.label}</a>
          {section.secondary ? <a className="ix-button ix-button--ghost" href={section.secondary.href}>{section.secondary.label}</a> : null}
        </div>
      </div>
    </section>
  );
}
